/**
 * One opinion dataset: who its numbers describe, its groups and their profiles, and
 * its statements with each group's agree % (specs/opinion-datasets.md, #1179).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Input, TextArea, Label, Button, Actions, StatusLine, FieldHelp } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import { getOpinionDataset, updateOpinionDataset, buildProfiles } from './opinionApi';
import GroupEditor from './GroupEditor';
import { Section, SectionTitle, Lead, Meta, Table, TableScroll, KindChip, ErrorText } from '../DatasetWorkspace/styles';

function Settings({ dataset, onSaved }) {
  const { t } = useTranslation();
  const [name, setName] = useState(dataset.name);
  const [note, setNote] = useState(dataset.populationNote || '');
  const [state, setState] = useState(null);

  const save = async () => {
    setState('saving');
    try {
      await updateOpinionDataset(dataset.id, { name, populationNote: note });
      setState('saved');
      onSaved();
    } catch (err) {
      setState({ error: err.message });
    }
  };

  return (
    <Section>
      <SectionTitle>{t('opinionDatasets.view.settings')}</SectionTitle>
      <Meta><span>{dataset.id}</span><span>{dataset.sourceFormat}</span><span>{dataset.language}</span></Meta>
      <Label htmlFor="opinion-name">{t('opinionDatasets.view.name')}</Label>
      <Input id="opinion-name" value={name} onChange={(e) => setName(e.target.value)} />
      <Label htmlFor="opinion-note">{t('opinionDatasets.view.populationNote')}</Label>
      <TextArea id="opinion-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      <FieldHelp>{t('opinionDatasets.view.populationNoteHelp')}</FieldHelp>
      <Actions>
        <Button type="button" $variant="primary" disabled={state === 'saving'} onClick={save}>{t('opinionDatasets.view.save')}</Button>
        {state === 'saved' && <StatusLine>{t('opinionDatasets.view.saved')}</StatusLine>}
      </Actions>
      {state?.error && <ErrorText>{state.error}</ErrorText>}
    </Section>
  );
}

function Statements({ statements, groups }) {
  const { t } = useTranslation();
  return (
    <Section>
      <SectionTitle>{t('opinionDatasets.view.statements')}</SectionTitle>
      <Meta>{t('opinionDatasets.view.statementsCount', { count: statements.length })}</Meta>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <th>{t('opinionDatasets.view.colId')}</th>
              <th>{t('opinionDatasets.view.colStatement')}</th>
              <th>{t('opinionDatasets.view.colTheme')}</th>
              <th>{t('opinionDatasets.view.colClass')}</th>
              {groups.map((g) => <th key={g.key}>{g.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.id}>
                <td>{s.externalId}</td>
                <td>
                  {s.text}
                  {s.kind === 'background' && <> <KindChip $kind="policy">{t('opinionDatasets.view.background')}</KindChip></>}
                  {s.authorType === 'seed' && s.kind !== 'background' && <> <KindChip $kind="fact">{t('opinionDatasets.view.seed')}</KindChip></>}
                </td>
                <td>{s.theme}</td>
                <td>{s.classification ? t(`opinionDatasets.classification.${s.classification}`) : ''}</td>
                {groups.map((g) => (
                  <td key={g.key}>{s.groupStats?.[g.key]?.agree != null ? `${s.groupStats[g.key].agree} %` : ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </Section>
  );
}

export default function OpinionDatasetView({ datasetId, onBack }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [building, setBuilding] = useState(null); // stage label while the job runs

  const load = useCallback(async () => {
    try {
      setData(await getOpinionDataset(datasetId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  const hasProfiles = data?.dataset.groups.some((g) => g.profile?.trim());

  const draft = async () => {
    if (hasProfiles && !(await confirm({ message: t('opinionDatasets.view.rebuildConfirm'), destructive: true }))) return;
    setBuilding('…');
    setError(null);
    try {
      await buildProfiles(datasetId, { onStage: setBuilding });
      await load();
    } catch (err) {
      setError(t('opinionDatasets.view.buildFailed', { message: err.message }));
    } finally {
      setBuilding(null);
    }
  };

  return (
    <>
      <Actions>
        <Button type="button" $variant="ghost" onClick={onBack}><ArrowLeft /> {t('opinionDatasets.view.back')}</Button>
      </Actions>
      {error && <ErrorText>{error}</ErrorText>}
      {data && (
        <>
          <Settings dataset={data.dataset} onSaved={load} />
          <Section>
            <SectionTitle>{t('opinionDatasets.view.groups')}</SectionTitle>
            <Lead>{t('opinionDatasets.view.groupsLead')}</Lead>
            <Actions>
              <Button type="button" $variant="primary" disabled={Boolean(building)} onClick={draft}>
                <Sparkles /> {t(hasProfiles ? 'opinionDatasets.view.rebuildProfiles' : 'opinionDatasets.view.buildProfiles')}
              </Button>
              {building && <StatusLine>{t('opinionDatasets.view.building', { stage: building })}</StatusLine>}
            </Actions>
          </Section>
          {data.dataset.groups.map((g) => (
            <GroupEditor key={g.key} datasetId={datasetId} group={g} onSaved={load} />
          ))}
          <Statements statements={data.statements} groups={data.dataset.groups} />
        </>
      )}
    </>
  );
}
